import { html, useEffect, useState } from '/vendor/htm-preact.module.js';
import { get, post, put, del } from '/api.js';
import { Bulletins } from '/bulletin.js';
import { Icon } from '/icons.js';
import { RoleMarks } from '/rolemark.js';
import { Stepper } from '/stepper.js';
import { ImageUpload, Picture } from '/imageupload.js';

/**
 * One venue's page.
 *
 * Running The Red Lion used to mean opening a screen called Admin and finding
 * it in a list — a venue manager holding no global authority had to visit the
 * place the *site* is managed. Making a venue is still a global-admin act;
 * running one is not, so it happens here, at its own URL.
 *
 * Which sections appear comes from `may`, computed by the server's own
 * resolver. The page never predicts a refusal: it offers what may be offered,
 * sends the request, and renders whatever answer comes back.
 */
export function VenueScreen({ venueId, view, onDone, onGo }) {
  const [venue, setVenue] = useState(null);
  const [error, setError] = useState(null);
  const [deviceName, setDeviceName] = useState('');
  const [address, setAddress] = useState('');
  const [postcode, setPostcode] = useState('');
  const [circleName, setCircleName] = useState('');
  const [code, setCode] = useState(null);
  const [deleting, setDeleting] = useState(false);
  // What is on here in the next month (issue #32). Its own fetch, and
  // failure-tolerant: a pub's page must still open when the league's read
  // model is unreachable, since everything else on it comes from our own log.
  const [upcoming, setUpcoming] = useState([]);
  // Everybody with an account, for putting somebody NEW on the roster.
  //
  // This is the one list on this page that cannot be narrowed. Issue #56 took
  // `/api/people` OFF the circle roles screen because a role there can only
  // go to somebody already in the circle, so every other account was noise.
  // A roster is the opposite: it exists to bring in somebody who is not here
  // yet, and the set of candidates IS everybody. Hence a search rather than
  // the `<select>` #56 removed -- 472 accounts on production is a list nobody
  // can read, but it is a list anybody can search.
  //
  // Its own fetch, and failure-tolerant for the same reason `upcoming` is: a
  // pub's page must still open when this cannot be had. Fetched only when the
  // caller may write the roster, since nobody else can act on it.
  const [people, setPeople] = useState([]);
  const [rosterSearch, setRosterSearch] = useState('');
  const [streamLabel, setStreamLabel] = useState('');
  const [streamLink, setStreamLink] = useState('');

  async function reload() {
    setVenue(await get(`/api/venues/${encodeURIComponent(venueId)}`));
  }

  useEffect(() => {
    reload().catch((e) => setError(e.detail || e.message));
  }, [venueId]);

  useEffect(() => {
    // Its own effect and failure-tolerant: what is on here comes from the
    // LEAGUE's read model, and everything else on this page comes from our
    // own log. A pub's page must still open when the archive is unreachable.
    get(`/api/venues/${encodeURIComponent(venueId)}/fixtures`)
      .then(setUpcoming)
      .catch(() => setUpcoming([]));
  }, [venueId]);

  useEffect(() => {
    // Only for somebody who may write the roster. `/api/people` is open to
    // any signed-in caller, so this is not a permission check -- it is a
    // round trip nobody else on this page has any use for.
    if (!(venue && venue.may && venue.may.roster)) return;
    get('/api/people').then(setPeople).catch(() => setPeople([]));
  }, [venueId, venue && venue.may && venue.may.roster]);

  async function act(fn) {
    setError(null);
    try {
      await fn();
      await reload();
    } catch (e) {
      setError(e.detail || e.message);
    }
  }

  const at = (suffix) => `/api/venues/${encodeURIComponent(venueId)}${suffix}`;

  /**
   * Give somebody a role here, or take one away — GitHub issue #55.
   *
   * `null` clears. Both named roles are revoked before either is granted, so
   * somebody promoted from roster keeper to manager ends up holding the
   * manager's set and NOT the union of the two: a union matches no bundle,
   * and `bundles.match` reports it as "Custom", which is accurate and useless
   * as a label on a screen offering exactly two roles. The circle roles
   * screen makes the same call for the same reason.
   *
   * MANAGER CASCADES. That is what carries the pub's authority into the
   * circles that play there; without it the grant is a different set with a
   * different meaning that happens to look the same on this row.
   */
  const VENUE_ROLES = {
    manager: { capability: '*', cascade: true },
    roster: { capability: 'venue.roster.write', cascade: false },
  };

  const setVenueRole = (userId, role) => act(async () => {
    for (const r of Object.values(VENUE_ROLES)) {
      // Revoking something they do not hold is not an error worth stopping
      // for -- it is the normal case for one of these two every time.
      await post('/api/grants/revoke', {
        subject_id: userId, scope_type: 'venue', scope_id: venueId,
        capability: r.capability,
      }).catch(() => {});
    }
    if (role) {
      await post('/api/grants', {
        subject_id: userId, scope_type: 'venue', scope_id: venueId,
        capability: VENUE_ROLES[role].capability,
        cascade: VENUE_ROLES[role].cascade,
      });
    }
  });

  if (error && !venue) {
    return html`
      <div class="screen">
        <button class="secondary" onClick=${onDone}>Back</button>
        <p class="error" data-testid="venue-error">${error}</p>
      </div>`;
  }
  if (!venue) return html`<p class="sub">Loading…</p>`;

  // Defaulted, not assumed. A response from a server older than this page
  // carries none of these, and reading .length off the first one killed the
  // render — which showed as the page never leaving "Loading…".
  const may = venue.may || {};
  const photo = venue.image;
  const circles = venue.circles || [];
  const live = venue.live || [];
  const roster = venue.roster || [];
  const grants = venue.grants || [];
  const board = venue.bulletins || [];
  // Say so rather than drawing an empty section that reads as "none".
  const behind = venue.may === undefined;

  const asking = venue.asking || [];
  const leagues = venue.leagues || [];
  const queue = venue.queue || {};
  const mine = (queue.entries || []).find((e) => e.user_id === venue.me);
  const place = mine
    ? queue.entries.findIndex((e) => e.entry_id === mine.entry_id) + 1 : 0;
  const hours = venue.hours || [];
  const tables = venue.tables || [];

  // Who could be added to the roster: everybody with an account who is not on
  // it already. Offering somebody twice would draw a button whose press is a
  // no-op, which reads as broken rather than as "they are already here".
  //
  // `/api/people` lists accounts only -- ghosts are excluded there by
  // design -- so a placeholder standing in for somebody in the archive can
  // never be put on a roster by this control. That is right: a ghost has no
  // credentials and cannot turn up. It also means the two people sharing a
  // name (issue #65) that a ghost-plus-account pair produces do not both
  // appear here, and the one offered is the one who can actually sign in.
  const onRoster = new Set(roster.map((p) => p.user_id));
  const addable = people.filter((p) => !onRoster.has(p.user_id));
  const rosterQuery = rosterSearch.trim().toLowerCase();
  const rosterMatches = rosterQuery
    ? addable.filter((p) => (p.name || '').toLowerCase().includes(rosterQuery))
    : [];

  // Issue #28: everything that CHANGES this pub lives behind one button,
  // and everything that describes it stays on the page. The split is by
  // `may.*` because that is already the line the server draws -- a manage
  // room holding controls somebody cannot use would be a worse page than
  // the mixed one it replaces.
  const canManage = !!(may.circle || may.roster || may.visibility
                       || may.delete || may.grant);

  // Issue #28: the venue reads like a circle now -- a header, the one thing
  // you came to find out, then ways in. `Venue Info`, `Circles` and `Manage`
  // are the same shape as a circle's `Matches`, `Chat` and `Manage`, because
  // they are the same kind of thing and a screen that looks different for no
  // reason has to be learned twice.
  const WAYS = [['info', 'help-circle', 'Venue Info'],
                ['circles', 'users', 'Circles']];
  // Beside Circles, and only when there is something to watch -- issue
  // #73. A way that opens onto "no livestreams" on all twenty-six pubs
  // teaches nobody anything; the Manage room is where one is added.
  if ((venue.streams || []).length > 0) {
    WAYS.push(['live', 'circle-dot', 'Livestream']);
  }
  if (canManage) WAYS.push(['manage', 'settings', 'Manage']);
  const way = WAYS.some(([id]) => id === view) ? view : 'info';

  const head = html`
      ${/* Where it is, as one tappable thing. A postcode is what somebody
            recognises AND what a map app can act on, which is why it is here
            beside the name rather than filed under an address. */''}
      ${(venue.postcode || venue.address) && html`
        <a class="pin" data-testid="venue-pin" href=${mapLink(venue)}
           target="_blank" rel="noreferrer">
          <${Icon} name="map-pin" />
          <span data-testid="venue-postcode">
            ${venue.postcode || venue.address}
          </span>
        </a>`}

      ${venue.manager && html`
        <p class="sub landlord" data-testid="venue-manager">
          ${`Run by ${venue.manager}`}
        </p>`}

      ${/* THE WAY IN, at the top of the pub's own page -- GitHub issue #70.
            It lived on one row of the venue-search list, so finding a pub,
            opening it, reading its hours and its tables and deciding you
            wanted to play there left you with no way to say so without
            going back out to the list you came in from.

            The server decides which of the three states this is (see
            `join` in api.py's venue detail), so a reload cannot forget
            that you already asked -- which is the bug the search list
            still has, tracking "Asked" in component state alone. */''}
      ${venue.join === 'open' && html`
        <button class="secondary" data-testid="venue-ask-to-join"
                onClick=${() => act(() => post('/api/requests', {
                  target_kind: 'venue', target_id: venueId,
                }))}>Ask to join</button>`}
      ${venue.join === 'asked' && html`
        <p class="sub" data-testid="venue-asked" style="text-transform:none">
          Asked — waiting for somebody here to answer.
        </p>`}
      `;

  const info = html`
      ${/* The pub's photo, at the top of Venue Info, which is the box Ben
            pointed at. `Picture` renders NOTHING when there is none, so a
            venue without one looks exactly as it did -- a placeholder frame
            would show a gap on all twenty-six pubs to advertise a feature
            one of them uses. Constrained by CSS as well as by
            `images.prepare`'s 1024px longest edge, so a wide photo and a
            tall one both sit in the same space. */''}
      <${Picture} imageId=${photo} alt=${`${venue.name}`}
                  className="venuephoto" />

      <${Bulletins} venueId=${venueId} board=${board} me=${venue.me}
                    mayPost=${!!may.visibility}
                    onChanged=${() => act(async () => {})} />

      ${/* Can I get a game? The one question somebody opens this page to
            answer, so it is above the things you have to press. */''}
      <div class="stack" data-testid="venue-availability">
        <label>The table</label>
        ${live.length === 0
          ? html`<p class="free" data-testid="venue-free">Free — nothing on.</p>`
          : html`
            <p class="sub" data-testid="venue-in-use">
              ${`In use — ${live.length} `}
              ${live.length === 1 ? 'game' : 'games'} being played.
            </p>
            ${live.map((m) => html`
              <button key=${m.match_id} class="secondary card rowbetween"
                      data-testid=${`venue-live-${m.match_id}`}
                      onClick=${() => onGo('live', m.match_id)}>
                <span>${m.players[0].name} v ${m.players[1].name}</span>
                <span class="mono">${m.score[0]}–${m.score[1]}</span>
              </button>`)}`}
      </div>

      ${/* You could only ever be put in the line by the tablet, as a guest.
            Somebody looking at the pub's page is exactly the person who wants
            to be in it as themselves. */''}
      ${queue.entries && html`
        <div class="stack" data-testid="venue-queue">
          <div class="rowbetween">
            <label>The queue</label>
            <span class="mono waiting" data-testid="venue-waiting">
              ${`${queue.waiting} waiting`}
            </span>
          </div>
          ${mine
            ? html`
              <div class="card rowbetween" data-testid="venue-in-queue">
                <span>${`You are ${place > 0 ? `#${place}` : 'in'} in the line`}</span>
                <button class="secondary" style="width:auto"
                        data-testid="venue-leave-queue"
                        onClick=${() => act(() => del(
                          at(`/queue/${encodeURIComponent(mine.entry_id)}`)))}>
                  Leave
                </button>
              </div>`
            : html`
              <button class="secondary" data-testid="venue-join-queue"
                      onClick=${() => act(() => post(at('/queue'), {}))}>
                Join the queue
              </button>`}
        </div>`}

      <${Panel} id="hours" title="Opening hours">
        ${hours.length === 7
          ? html`
            <div class="week" data-testid="venue-hours">
              ${DAYS.map((day, i) => html`
                <div class="rowbetween" key=${day}>
                  <span class="mono">${day}</span>
                  <span data-testid=${`venue-hours-${i}`}>
                    ${hours[i] || '—'}
                  </span>
                </div>`)}
            </div>`
          : html`<p class="empty" data-testid="venue-no-hours">
                   Opening hours are not set.
                 </p>`}
      </${Panel}>

      <${Panel} id="tables" title="Table info">
        ${tables.some(anyTableFact)
          ? html`
            <div class="stack" data-testid="venue-tableinfo">
              ${tables.map((t) => html`
                <div class="card stack" key=${t.table_id}
                     data-testid=${`venue-table-${t.table_id}`}>
                  <div class="rowbetween">
                    <span>${t.name}</span>
                    <span class=${t.busy ? 'mono' : 'mono free'}
                          data-testid="venue-table-state">
                      ${t.busy ? 'in use' : 'free'}
                    </span>
                  </div>
                  ${[['Size', t.size], ['Cloth', t.cloth],
                     ['Per game', t.price]]
                    .filter(([, value]) => value)
                    .map(([label, value]) => html`
                      <div class="rowbetween" key=${label}>
                        <span class="mono">${label}</span>
                        <span data-testid=${`venue-table-${label.toLowerCase()}`}>
                          ${value}
                        </span>
                      </div>`)}
                </div>`)}
            </div>`
          : html`<p class="empty" data-testid="venue-no-tableinfo">
                   Nothing recorded about the tables yet.
                 </p>`}
      </${Panel}>

      `;

  const circlesView = html`
      <${Panel} id="circles" title="Circles" open>
        ${circles.length === 0
          ? html`<p class="empty" data-testid="venue-no-circles">
                   No circles at this venue yet.
                 </p>`
          : circles.map((c) => html`
              ${/* A TEAM goes to its team page; only an ordinary circle goes
                    to the circle screen. They are not interchangeable — the
                    circle screen asks for a friendlies leaderboard, which the
                    server refuses for a team, so this used to end at a red
                    note saying where the stats really live instead of taking
                    you there. */''}
              <button key=${c.circle_id} class="secondary card rowbetween"
                      data-testid=${`venue-circle-${c.circle_id}`}
                      onClick=${() => (c.team_of
                        ? onGo('team', c.team_of, c.circle_id)
                        : onGo('circle', c.circle_id))}>
                <span>${c.name}</span>
                ${/* How strong it is, on the same line: the whole point of
                      the list is choosing which one to ask to join. */''}
                <span class="mono standard"
                      data-testid=${`venue-circle-elo-${c.circle_id}`}>
                  ${c.elo_average == null
                    ? 'unrated'
                    : `${c.elo_low}–${c.elo_high} · avg ${c.elo_average}`}
                </span>
              </button>`)}
      </${Panel}>`;

  const streams = venue.streams || [];

  /* WHAT IS PLAYING, under the video — issue #73. The venue payload
     already carries `live`, scored by the kiosk tablet, so the match half
     of Ben's request needs no new plumbing: "the current match pulling
     the match data from a linked kiosk venue tablet" is this list. */
  const watching = html`
      <div class="stack" data-testid="venue-live-panel">
        ${streams.length === 0
          ? html`<p class="empty" data-testid="venue-live-none">
                   Nothing is being streamed here.
                 </p>`
          : streams.map((s) => html`
            <div key=${s.stream_id} class="stack"
                 data-testid=${`venue-stream-${s.stream_id}`}>
              <label>${s.label}</label>
              ${/* `s.embed` was built from constants by
                    cueforge/livestream.py -- what somebody pasted is in
                    `s.link` and never reaches this attribute. The CSP
                    names the two hosts it may load, so anything else is
                    refused by the browser as well. */''}
              <div class="videowrap">
                <iframe data-testid=${`venue-stream-frame-${s.stream_id}`}
                        src=${s.embed} title=${s.label}
                        ${/* NO `autoplay`. Granting it was thoughtless:
                              Twitch's player starts on its own, so opening
                              this tab began playing sound, and a venue with
                              two streams began two. The URL says
                              `autoplay=false` as well -- this is the half
                              the page controls. */''}
                        allow="fullscreen; picture-in-picture"
                        allowfullscreen loading="lazy"
                        referrerpolicy="no-referrer"></iframe>
              </div>
            </div>`)}

        <div class="stack" data-testid="venue-live-matches">
          <label>On the table</label>
          ${live.length === 0
            ? html`<p class="empty" data-testid="venue-live-nomatch">
                     No match is being scored here right now.
                   </p>`
            : live.map((m) => html`
              <div key=${m.match_id} class="card rowbetween"
                   data-testid=${`venue-live-match-${m.match_id}`}>
                <span>${m.players.map((p) => p.name).join(' v ')}</span>
                <span class="mono">
                  ${`${m.score[0]}–${m.score[1]}`}
                  <span class="sub">${` (race to ${m.race_to})`}</span>
                </span>
              </div>`)}
        </div>
      </div>`;

  const manage = html`
      ${/* A marker for the room itself, drawn whatever the caller may do.
            Without it there is nothing that says "the manage room rendered"
            -- `venue-screen` wraps both -- so a test would have to wait on
            some section that happens to be there for THAT user. */''}
      <span hidden data-testid="venue-manage-room"></span>

      ${/* LIVESTREAMS -- issue #73. Behind `may.visibility`, the same
            switch that decides the venue's photo and whether it is
            listed: pointing at a stream is how the pub presents itself.

            The link is checked by the SERVER, which is where the rule
            has to live -- cueforge/livestream.py rebuilds the embed URL
            from constants rather than trusting anything typed here. This
            box only reports what it was told. */''}
      ${may.visibility && html`
        <div class="stack" data-testid="venue-streams">
          <label>Livestreams</label>
          <p class="sub" style="text-transform:none">
            A YouTube or Twitch link to a stream this pub already runs.
            It plays here with the current match under it.
          </p>
          ${(venue.streams || []).map((st) => html`
            <div key=${st.stream_id} class="card rowbetween"
                 data-testid=${`venue-stream-row-${st.stream_id}`}>
              <span>${st.label}<span class="sub"> · ${st.link}</span></span>
              <button class="secondary" style="width:auto"
                      data-testid=${`venue-stream-remove-${st.stream_id}`}
                      onClick=${() => act(() => del(
                        at(`/streams/${encodeURIComponent(st.stream_id)}`)))}>
                Remove
              </button>
            </div>`)}
          ${/* THE LINK FIRST, and on its own line. Two unlabelled boxes
                side by side with the NAME on the left is what put Ben in
                the wrong one: he pasted the URL, pressed Add, and nothing
                happened, because the empty name box was disabling the
                button and saying nothing about it. The link is the thing
                somebody has in their clipboard; the name is optional and
                now says so. */''}
          <label for="stream-link">Link</label>
          <input id="stream-link" data-testid="venue-stream-link"
                 value=${streamLink} placeholder="https://twitch.tv/…"
                 onInput=${(e) => setStreamLink(e.target.value)} />
          <label for="stream-label">Name (optional)</label>
          <input id="stream-label" data-testid="venue-stream-label"
                 value=${streamLabel} maxlength="80"
                 placeholder=${streamLink.trim()
                                ? 'Taken from the link'
                                : 'Table 1'}
                 onInput=${(e) => setStreamLabel(e.target.value)} />
          ${/* Enabled on the LINK alone. A disabled button that explains
                nothing is indistinguishable from a broken one. */''}
          <button class="secondary" data-testid="venue-stream-add"
                  disabled=${!streamLink.trim()}
                  onClick=${() => act(async () => {
                    await post(at('/streams'),
                               { label: streamLabel, link: streamLink });
                    setStreamLabel(''); setStreamLink('');
                  })}>Add livestream</button>

          ${/* Who the cameras are for -- Ben's follow-up on the same issue.
                INSIDE this box rather than beside the venue's own
                visibility switch further down, because it is a fact about
                the streams and belongs with them: read next to "Who can
                see this venue" it looks like the same question asked
                twice.

                It can only ever NARROW. A roster-only venue is not opened
                up by setting this to Anyone -- the streams are served
                inside the venue's own payload, which that person never
                reaches. The wording says "Anyone who can see this pub"
                rather than "Public" for exactly that reason. */''}
          <label>Who can watch</label>
          <div class="seg" data-testid="venue-stream-visibility">
            ${[['public', 'Anyone who can see this pub'],
               ['roster', 'The roster only']].map(([id, label]) => html`
              <button key=${id}
                      aria-pressed=${venue.stream_visibility === id}
                      data-testid=${`venue-stream-visibility-${id}`}
                      onClick=${() => act(() => put(
                        at('/stream-visibility'), { visibility: id }))}>
                ${label}
              </button>`)}
          </div>
        </div>`}
      ${may.circle && html`
        <div class="stack" data-testid="venue-add-circle">
          <label>Add a circle</label>
          <div class="row pair">
            <input data-testid="venue-circle-name" value=${circleName}
                   placeholder="Tuesday night"
                   onInput=${(e) => setCircleName(e.target.value)} />
            <button class="secondary" data-testid="venue-circle-create"
                    onClick=${() => act(async () => {
                      await post('/api/circles',
                                 { name: circleName, venue_id: venueId });
                      setCircleName('');
                    })}>Add</button>
          </div>
        </div>`}

      ${/* The roster is management, not information. Who drinks in a pub is
            not something the pub's page owes a player; it was on here only
            because managing it had nowhere else to live. */''}
      ${may.roster && html`
        <div class="stack" data-testid="venue-roster-section">
          <label>Roster</label>
          ${roster.length === 0
            ? html`<p class="empty">Nobody on the roster yet.</p>`
            : roster.map((p) => html`
                <div key=${p.user_id} class="card rowbetween"
                     data-testid=${`venue-roster-${p.user_id}`}>
                  <span><${RoleMarks} roles=${p.roles} only=${1} />${p.name}</span>
                  <button class="secondary" style="width:auto"
                          onClick=${() => act(() => del(
                            at(`/roster/${encodeURIComponent(p.user_id)}`)))}>
                    Remove
                  </button>
                </div>`)}

          ${/* PUTTING somebody on. `POST /roster` had existed all along with
                nothing in the frontend calling it -- the only `/roster` call
                in the whole web directory was the DELETE above -- so this
                section could take people off and never put anybody on.

                That is not a cosmetic gap. "Give somebody authority" below
                builds its list from the roster, so a pub whose roster was one
                person could only ever make a manager of that person, and the
                advice it gave when it ran out ("Add people to the roster
                above first") pointed here, at a section with no way to add.

                A SEARCH, not a dropdown: see `people`'s own comment on why
                this is the one list here that cannot be narrowed, and why
                issue #56 removing a `<select>` of every account does not
                argue for one here. */''}
          <div class="stack" data-testid="venue-roster-add">
            <label>Add somebody to the roster</label>
            <input data-testid="venue-roster-search" value=${rosterSearch}
                   placeholder="Search people"
                   onInput=${(e) => setRosterSearch(e.target.value)} />
            ${rosterQuery && rosterMatches.length === 0 && html`
              <p class="empty" data-testid="venue-roster-add-none">
                Nobody with an account is called that.
              </p>`}
            ${!rosterQuery && html`
              ${/* The prompt rather than the wall, the same choice the people
                    search made: an empty query over every account is a column
                    of hundreds of controls to reach one of them. */''}
              <p class="sub" data-testid="venue-roster-add-browse">
                ${`${addable.length} people have an account. `
                  + `Type a name to find one.`}
              </p>`}
            ${rosterMatches.map((p) => html`
              <div key=${p.user_id} class="card rowbetween"
                   data-testid=${`venue-roster-candidate-${p.user_id}`}>
                <span><${RoleMarks} roles=${p.roles} only=${1} />${p.name}</span>
                <button class="secondary" style="width:auto"
                        data-testid=${`venue-roster-add-${p.user_id}`}
                        onClick=${() => act(async () => {
                          await post(at('/roster'), { user_id: p.user_id });
                          setRosterSearch('');
                        })}>
                  Add
                </button>
              </div>`)}
          </div>
        </div>`}

      ${/* The pub's own photo -- GitHub issue #66. Behind the same
             capability as the noticeboard and the visibility switches: it
             already means "decide how this venue presents itself", and
             inventing a capability for a photo would widen a vocabulary that
             is deliberately closed. */''}
      ${may.visibility && html`
        <${ImageUpload} kind="venue" subjectId=${venueId}
                        label="A photo of the pub" />`}

      ${may.visibility && html`
        <div class="stack" data-testid="venue-settings">
          <label>Who can see the games</label>
          <div class="card rowbetween">
            <span data-testid="venue-visibility">${venue.visibility}</span>
            <button class="secondary" style="width:auto"
                    data-testid="venue-visibility-toggle"
                    onClick=${() => act(() => put(at('/visibility'), {
                      visibility: venue.visibility === 'public'
                        ? 'roster' : 'public' }))}>
              Make ${venue.visibility === 'public' ? 'roster only' : 'public'}
            </button>
          </div>

          <label>Findable by others</label>
          <div class="card rowbetween">
            <span data-testid="venue-listed">${venue.listed ? 'yes' : 'no'}</span>
            <button class="secondary" style="width:auto"
                    data-testid="venue-listed-toggle"
                    onClick=${() => act(() => put(at('/listing'),
                                                  { listed: !venue.listed }))}>
              ${venue.listed ? 'Stop listing it' : 'List it'}
            </button>
          </div>
          <p class="sub">
            Listing lets people find this venue and ask to join. It does not
            publish the games — that is "Who can see the games", above.
          </p>

          <label>Where it is</label>
          <div class="card rowbetween">
            <span class="sub" data-testid="venue-address">
              ${[venue.postcode, venue.address].filter(Boolean).join(' · ')
                || 'No address set'}
            </span>
            <span class="sub">${venue.lat != null ? 'pinned' : 'no pin'}</span>
          </div>
          <button class="secondary" data-testid="venue-here"
                  onClick=${() => act(() => new Promise((resolve, reject) => {
                    if (!navigator.geolocation) {
                      reject(new Error('This device cannot report a location'));
                      return;
                    }
                    navigator.geolocation.getCurrentPosition(
                      (pos) => resolve(put(at('/location'), {
                        address: venue.address,
                        lat: pos.coords.latitude,
                        lon: pos.coords.longitude })),
                      () => reject(new Error('Location was refused')));
                  }))}>Use my location</button>
          <input data-testid="venue-postcode-input" value=${postcode}
                 placeholder="Postcode" maxlength="10"
                 onInput=${(e) => setPostcode(e.target.value)} />
          <div class="row pair">
            <input data-testid="venue-address-input" value=${address}
                   placeholder="12 High Street, Anytown"
                   onInput=${(e) => setAddress(e.target.value)} />
            <button class="secondary" data-testid="venue-address-save"
                    onClick=${() => act(async () => {
                      // Geocoded HERE, not on the server: the box makes no
                      // outbound call and needs no key, and the only thing sent
                      // is a pub's address. Failing is normal — the address is
                      // kept and the venue lists without a distance rather than
                      // not at all.
                      let lat = null; let lon = null;
                      try {
                        const r = await fetch(
                          'https://nominatim.openstreetmap.org/search?format=json&limit=1&q='
                          + encodeURIComponent(
                            [address, postcode].filter(Boolean).join(', ')),
                          { headers: { 'Accept': 'application/json' } });
                        const hits = await r.json();
                        if (hits.length) {
                          lat = parseFloat(hits[0].lat);
                          lon = parseFloat(hits[0].lon);
                        }
                      } catch (e) { /* keep the address, skip the pin */ }
                      await put(at('/location'),
                                { address, postcode, lat, lon });
                      setAddress(''); setPostcode('');
                    })}>Save</button>
          </div>
        </div>`}

      ${(asking.length > 0 || may.circle) && html`
        <div class="stack" data-testid="venue-asking">
          <label>Circles asking to play here</label>
          ${asking.length === 0
            ? html`<p class="empty" data-testid="venue-none-asking">
                     Nobody has asked.
                   </p>`
            : asking.map((ask) => html`
                <div class="card rowbetween" key=${ask.circle_id}
                     data-testid=${`venue-ask-${ask.circle_id}`}>
                  <span>${ask.name}</span>
                  <span class="row" style="width:auto">
                    <button class="secondary" style="width:auto"
                            data-testid=${`venue-ask-yes-${ask.circle_id}`}
                            onClick=${() => act(() => post(
                              at(`/circles/${encodeURIComponent(ask.circle_id)}`),
                              { accept: true }))}>Take them on</button>
                    <button class="secondary" style="width:auto"
                            data-testid=${`venue-ask-no-${ask.circle_id}`}
                            onClick=${() => act(() => post(
                              at(`/circles/${encodeURIComponent(ask.circle_id)}`),
                              { accept: false }))}>No</button>
                  </span>
                </div>`)}
        </div>`}

      ${may.visibility && leagues.length > 0 && html`
        <div class="stack" data-testid="venue-leagues">
          <label>Leagues</label>
          ${/* Only leagues this caller is in: a pub's page must not name one
                they were never given a code to. */''}
          ${leagues.map((l) => html`
            <div class="stack" key=${l.league_id}>
              <div class="card rowbetween"
                   data-testid=${`venue-league-${l.league_id}`}>
                <span>${l.name}</span>
                ${l.state === 'in'
                  ? html`
                    <span class="row pair" style="width:auto">
                      <span class="mono free">in it</span>
                      ${/* The pub's own way out of a league (issue #23).
                            withdraw_league_entry has always allowed a venue
                            manager to do this and there was simply no
                            button -- the same gap the teams below had, one
                            row up. Withdrawing the VENUE takes its teams
                            with it, since league_teams() requires the
                            circle's venue to be in the league, so the
                            server's own refusals are what this leans on
                            rather than predicting anything. */''}
                      <button class="secondary" style="width:auto"
                              data-testid=${`venue-league-withdraw-${l.league_id}`}
                              onClick=${() => act(() => del(
                                `/api/leagues/${encodeURIComponent(l.league_id)}`
                                + `/entries/venue/${encodeURIComponent(venueId)}`))}>
                        Withdraw
                      </button>
                    </span>`
                  : l.state === 'asked'
                    ? html`<span class="mono" data-testid="venue-league-asked">
                             applied
                           </span>`
                    : html`
                      <button class="secondary" style="width:auto"
                              data-testid=${`venue-apply-${l.league_id}`}
                              onClick=${() => act(() => post(
                                `/api/leagues/${encodeURIComponent(l.league_id)}/entries`,
                                { kind: 'venue', subject_id: venueId }))}>
                        Apply
                      </button>`}
              </div>

              ${/* A circle becomes a Team by being entered into a league its
                    OWN venue already plays in — never one the venue has only
                    asked to join, and never one it is not in at all. */''}
              ${l.state === 'in' && circles.length > 0 && (() => {
                // Which teams are in THIS league, and which merely could be.
                // They used to be one undifferentiated run of cards sitting
                // under the league's own card, so "which teams are in which
                // league" -- the question this section exists to answer --
                // could only be worked out by reading the badge on every row
                // (GitHub issue #14). Split, counted and named instead.
                const stateOf = (c) => {
                  const entry = (c.leagues || [])
                    .find((cl) => cl.league_id === l.league_id);
                  return entry ? entry.state : 'no';
                };
                const playing = circles.filter((c) => stateOf(c) !== 'no');
                const could = circles.filter((c) => stateOf(c) === 'no');
                const row = (c) => {
                  const state = stateOf(c);
                  return html`
                      <div class="card rowbetween" key=${c.circle_id}
                           data-testid=${`venue-team-${l.league_id}-${c.circle_id}`}>
                        <span>${c.name}</span>
                        ${state === 'in'
                          ? html`
                            <span class="row pair" style="width:auto">
                              <span class="mono free">in it</span>
                              ${/* The pub's own way out. `withdraw_league_entry`
                                    has always allowed a venue manager to do
                                    this; there was simply no button, so the
                                    only way out was to ask the committee. */''}
                              <button class="secondary" style="width:auto"
                                      data-testid=${`venue-team-withdraw-${l.league_id}-${c.circle_id}`}
                                      onClick=${() => act(() => del(
                                        `/api/leagues/${encodeURIComponent(l.league_id)}`
                                        + `/entries/circle/${encodeURIComponent(c.circle_id)}`))}>
                                Withdraw
                              </button>
                            </span>`
                          : state === 'asked'
                            ? html`
                              <span class="mono"
                                    data-testid=${`venue-team-asked-${l.league_id}-${c.circle_id}`}>
                                applied
                              </span>`
                            : html`
                              <button class="secondary" style="width:auto"
                                      data-testid=${`venue-team-apply-${l.league_id}-${c.circle_id}`}
                                      onClick=${() => act(() => post(
                                        `/api/leagues/${encodeURIComponent(l.league_id)}/entries`,
                                        { kind: 'circle', subject_id: c.circle_id }))}>
                                Enter as a team
                              </button>`}
                      </div>`;
                };
                return html`
                  <div class="stack indent"
                       data-testid=${`venue-teams-${l.league_id}`}>
                    <p class="sub" data-testid=${`venue-teams-count-${l.league_id}`}>
                      ${playing.length === 0
                        ? `No teams from this pub in ${l.name} yet`
                        : `${playing.length} ${playing.length === 1
                             ? 'team' : 'teams'} from this pub in ${l.name}`}
                    </p>
                    ${playing.map(row)}
                    ${/* Quieter, and second: these are not in the league, so
                          they are not an answer to "which teams are in it" --
                          they are the way to add one. */''}
                    ${could.length > 0 && html`
                      <p class="sub"
                         data-testid=${`venue-teams-could-${l.league_id}`}>
                        Other circles here, which could enter
                      </p>`}
                    ${could.map(row)}
                  </div>`;
              })()}
            </div>`)}
        </div>`}

      ${may.visibility && html`
        <${VenueFacts} venue=${venue} at=${at} act=${act} />`}

      ${may.delete && html`
        <div class="stack" data-testid="venue-delete-section">
          <label>Delete this venue</label>
          <p class="sub">
            Takes it off every list. Matches, ratings and the noticeboard
            already recorded here are kept, not undone.
          </p>
          ${/* The server refuses a pub that still has circles at it, or one
                that is in a league, and says which -- so the page does not
                predict either. It sends the request and renders the answer,
                the same rule the rest of this screen follows. */''}
          ${deleting
            ? html`
              <div class="row pair">
                <button class="secondary" data-testid="venue-delete-cancel"
                        onClick=${() => setDeleting(false)}>Cancel</button>
                <button data-testid="venue-delete-confirm"
                        style="color:var(--danger);border-color:var(--danger)"
                        onClick=${() => act(async () => {
                          await del(`/api/venues/${encodeURIComponent(venueId)}`);
                          onDone();
                        })}>Yes, delete it</button>
              </div>`
            : html`
              <button class="secondary" data-testid="venue-delete"
                      onClick=${() => setDeleting(true)}>Delete this venue</button>`}
        </div>`}

      ${may.grant && html`
        <div class="stack" data-testid="venue-authority">
          <label>Authority here</label>
          ${grants.length === 0 && html`
            <p class="empty" data-testid="venue-no-grants">
              Nobody holds authority scoped to this venue. Site admins can
              still manage it.
            </p>`}
          ${grants.map((p) => html`
            <div key=${p.user_id} class="card rowbetween"
                 data-testid=${`venue-grant-${p.user_id}`}>
              <span><${RoleMarks} roles=${p.roles} only=${1} />${p.name}</span>
              <span class="sub">${p.bundle}</span>
              ${p.bundle === 'Table' && html`
                <button class="secondary" style="width:auto"
                        data-testid="device-code"
                        onClick=${() => act(async () => {
                          const r = await post('/api/invites',
                                               { bind_user_id: p.user_id });
                          setCode(r.code);
                        })}>Get sign-in code</button>`}
              ${/* A TABLE is not a person and its bundle is not a role
                    somebody was given -- it is what the tablet by the pool
                    table holds, enrolled below. Offering "take it away" here
                    would un-authorise the tablet from a screen about staff,
                    so the device keeps only its sign-in code. */''}
              ${p.bundle !== 'Table' && html`
                <button class="secondary" style="width:auto"
                        data-testid=${`venue-role-clear-${p.user_id}`}
                        onClick=${() => setVenueRole(p.user_id, null)}>
                  Take it away
                </button>`}
            </div>`)}
        </div>

        ${/* GIVING somebody authority here — GitHub issue #55. The section
              above listed who held what and offered no way to change it, and
              said so: "Site admins can still manage it", which is true and
              was the only route. So a landlord could see that nobody ran
              their pub and could do nothing about it.

              Offered from the pub's OWN ROSTER rather than from every
              account on the instance — the same call the circle roles screen
              makes (issue #56). Somebody who does not drink here is not
              somebody you are about to make the manager of here, and a list
              of 472 to find one of six is not a list.

              Two roles, named for what they DO. `Manager` is the venue
              wildcard WITH cascade, which is what reaches the pub's circles;
              granting it without cascade produces a set matching no bundle,
              which `bundles.match` correctly reports as "Custom" and which
              reads on this screen as a role nobody chose. */''}
        <div class="stack" data-testid="venue-give-authority">
          <label>Give somebody authority</label>
          ${(venue.roster || []).filter(
              (r) => !grants.some((g) => g.user_id === r.user_id)).length === 0
            ? html`
              <p class="sub" data-testid="venue-nobody-to-grant">
                Everybody on the roster already holds something here. Add
                people to the roster above first.
              </p>`
            : (venue.roster || [])
                .filter((r) => !grants.some((g) => g.user_id === r.user_id))
                .map((r) => html`
                  <div key=${r.user_id} class="card stack"
                       data-testid=${`venue-grantable-${r.user_id}`}>
                    <span><${RoleMarks} roles=${r.roles} only=${1} />${r.name}</span>
                    <div class="row pair">
                      <button class="secondary"
                              data-testid=${`venue-make-manager-${r.user_id}`}
                              onClick=${() => setVenueRole(r.user_id,
                                                           'manager')}>
                        Runs the pub
                      </button>
                      <button class="secondary"
                              data-testid=${`venue-make-roster-${r.user_id}`}
                              onClick=${() => setVenueRole(r.user_id,
                                                           'roster')}>
                        Keeps the roster
                      </button>
                    </div>
                  </div>`)}
        </div>

        <div class="stack" data-testid="venue-tablet">
          <label>The tablet by the table</label>
          <div class="row pair">
            <input data-testid="device-name" value=${deviceName}
                   placeholder="Red Lion tablet"
                   onInput=${(e) => setDeviceName(e.target.value)} />
            <button class="secondary" data-testid="device-enrol"
                    onClick=${() => act(async () => {
                      await post(at('/devices'), { name: deviceName });
                      setDeviceName('');
                    })}>Enrol</button>
          </div>
          ${code && html`
            <div class="card" data-testid="device-code-shown">
              <p class="sub">Open the app on the tablet and redeem this. It is
                 shown once, and the tablet keeps the name you gave it.</p>
              <code>${code}</code>
            </div>`}
        </div>`}`;

  return html`
    <div class="screen" data-testid="venue-screen">
      <div class="rowbetween">
        <h2 data-testid="venue-title">${venue.name}</h2>
        <span class="row pair" style="width:auto">
          ${/* Only for somebody who can actually change something, and only
                on the page itself -- a Manage button inside Manage is a
                control that does nothing. */''}
          ${/* Manage is a WAY now (issue #28), beside Venue Info and
                Circles, so this is just Back -- and Back means the venues
                list, because the ways are all one screen rather than pages
                you step into. */''}
          <button class="secondary" style="width:auto"
                  data-testid="venue-back" onClick=${onDone}>Back</button>
        </span>
      </div>
      ${/* Under the header, so BOTH halves show them. These used to sit in
            the page fragment -- which was fine while every control was on
            the page, and became invisible the moment the controls that FAIL
            moved into the manage room. A refused delete then looked exactly
            like a button that did nothing. */''}
      ${error && html`<p class="error" data-testid="venue-error">${error}</p>`}
      ${behind && html`
        <p class="sub" data-testid="venue-behind">
          Some of this venue's details need a newer server than the one
          running, so they are not shown.
        </p>`}

      ${head}

      ${/* The ways in, under who runs it -- the spot a circle puts its own
            (issue #28). Manage is one of them rather than a button off in
            the header, so the two screens are learned once. */''}
      <nav class="ways" data-testid="venue-ways">
        ${WAYS.map(([id, icon, label]) => html`
          <button key=${id} data-testid=${`venue-go-${id}`}
                  aria-current=${way === id ? 'page' : undefined}
                  onClick=${() => onGo('venue', venueId,
                                       id === 'info' ? null : id)}>
            <${Icon} name=${icon} size=${18} />
            <span>${label}</span>
          </button>`)}
      </nav>

      ${way === 'manage' ? manage
        : way === 'circles' ? circlesView
          : way === 'live' ? watching
            : info}

      ${/* What is booked here in the next month (issue #32). Directly under
            "can I get a game", because it is the same question asked about
            Thursday: a pub with a home fixture on Thursday is not free on
            Thursday, and that is what somebody wants to know before setting
            off. Absent, not empty, when there is nothing -- a heading over
            "no fixtures" reads as a page that failed to load. */''}
      ${upcoming.length > 0 && html`
        <div class="stack" data-testid="venue-upcoming">
          <label>Coming up here</label>
          ${upcoming.map((f) => html`
            <button key=${f.fixture_id} class="secondary card rowbetween"
                    data-testid=${`venue-upcoming-${f.fixture_id}`}
                    onClick=${() => onGo('fixture', f.league_id,
                                         f.fixture_id)}>
              <span style="text-align:left;min-width:0">
                <span style="display:block;text-transform:none">
                  ${`${f.home.name} v ${f.away.name}`}
                </span>
                <span class="sub" style="text-transform:none">
                  ${f.division ? `${f.league} · ${f.division}` : f.league}
                </span>
              </span>
              ${/* The time when the league states one, the date alone when
                    it does not. A cup carries 00:00 at source and so does
                    every fixture written before the archive kept the hour --
                    printing "00:00" would be inventing a midnight kick-off
                    for most of the calendar (issue #32). */''}
              <span class="sub mono" style="white-space:nowrap;
                                            text-align:right">
                <span style="display:block">${f.played_on}</span>
                ${timeOf(f.starts_at) && html`
                  <span style="display:block"
                        data-testid=${`venue-upcoming-time-${f.fixture_id}`}>
                    ${timeOf(f.starts_at)}
                  </span>`}
              </span>
            </button>`)}
        </div>`}
    </div>`;
}


/** A fixture's kick-off, or "" when the league never said.
 *
 * Midnight IS "never said" here, and treating it as a time would put a
 * 00:00 kick-off on almost every row: cup fixtures carry no hour at source,
 * and neither does any fixture scraped before the archive kept one. A pub
 * league plays in the evening, so a genuine midnight fixture does not exist
 * to be wrongly hidden by this.
 */
function timeOf(startsAt) {
  const at = String(startsAt || '');
  const clock = at.slice(11, 16);
  return clock && clock !== '00:00' ? clock : '';
}

const DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

/**
 * What the landlord fills in: the week, and what you are playing on.
 *
 * Every field is free text, including the price. Real answers are "12–3, 6–11",
 * "Closed", "£1", "Free before 6" — a schema of open and close times, or a
 * decimal price, cannot hold any of those, so it would be filled in with
 * something untrue instead.
 */
function VenueFacts({ venue, at, act }) {
  const [week, setWeek] = useState(
    () => (venue.hours && venue.hours.length === 7
      ? [...venue.hours] : Array(7).fill('')));
  // The pub-wide form sets how many and what they are all like; a table that
  // differs is set on its own, on the venue's own page.
  const all = venue.tables || [];
  const t = all[0] || {};
  const [tables, setTables] = useState(all.length ? String(all.length) : '');
  const [size, setSize] = useState(t.size || '');
  const [cloth, setCloth] = useState(t.cloth || '');
  const [price, setPrice] = useState(t.price || '');
  const [wait, setWait] = useState(
    () => Math.round(((venue.queue || {}).wait_seconds || 300) / 60));
  const [hold, setHold] = useState(
    () => Math.round(((venue.table || {}).lasts_seconds || 900) / 60));

  const day = (i, v) => setWeek(week.map((d, j) => (j === i ? v : d)));

  return html`
    <div class="stack" data-testid="venue-facts">
      <label>Opening hours</label>
      ${DAYS.map((name, i) => html`
        <div class="row pair" key=${name}>
          <span class="mono daylabel">${name}</span>
          <input data-testid=${`venue-hours-input-${i}`} value=${week[i]}
                 maxlength="40" placeholder="12–3, 6–11"
                 onInput=${(e) => day(i, e.target.value)} />
        </div>`)}
      <button class="secondary" data-testid="venue-hours-save"
              onClick=${() => act(() => put(at('/hours'), { hours: week }))}>
        Save hours
      </button>

      <label>Queue</label>
      <p class="sub">
        How long somebody called to the table has to turn up before the next
        person is called instead.
      </p>
      <${Stepper} testid="venue-queue-wait" label="minutes"
                  value=${wait} min=${1} max=${30}
                  format=${(m) => `${m} min`}
                  onChange=${(m) => { setWait(m);
                    act(() => put(at('/queue-wait'), { minutes: m })); }} />

      <label>Free play</label>
      <p class="sub">
        How long "just having a game" holds the table before it frees itself.
        It has to expire — a claim nobody releases would stop the queue.
      </p>
      <${Stepper} testid="venue-claim-wait" label="minutes"
                  value=${hold} min=${5} max=${120} step=${5}
                  format=${(m) => `${m} min`}
                  onChange=${(m) => { setHold(m);
                    act(() => put(at('/claim-wait'), { minutes: m })); }} />

      <label>Table info</label>
      <input data-testid="venue-tables-input" value=${tables} inputmode="numeric"
             placeholder="How many tables"
             onInput=${(e) => setTables(e.target.value)} />
      <input data-testid="venue-size-input" value=${size} maxlength="40"
             placeholder="Size — 7ft, 6ft"
             onInput=${(e) => setSize(e.target.value)} />
      <input data-testid="venue-cloth-input" value=${cloth} maxlength="60"
             placeholder="Cloth — Strachan 6811, new in March"
             onInput=${(e) => setCloth(e.target.value)} />
      <input data-testid="venue-price-input" value=${price} maxlength="40"
             placeholder="Per game — £1, free before 6"
             onInput=${(e) => setPrice(e.target.value)} />
      <button class="secondary" data-testid="venue-tables-save"
              onClick=${() => act(() => put(at('/tables'), {
                tables: tables === '' ? null : Number(tables),
                size, cloth, price,
              }))}>Save table info</button>
    </div>`;
}

/**
 * A heading you press to see what is under it.
 *
 * Three of these rather than three screens: opening hours and what the cloth
 * is are things you check once and then never again, and they should not push
 * the noticeboard and the table's state off the top of the page. Circles
 * starts open because it is the one people came to use.
 */
function Panel({ id, title, open = false, children }) {
  const [shown, setShown] = useState(open);
  return html`
    <div class="stack" data-testid=${`venue-panel-${id}`}>
      <button class=${`secondary panel ${shown ? 'open' : ''}`}
              aria-expanded=${shown}
              data-testid=${`venue-open-${id}`}
              onClick=${() => setShown(!shown)}>
        <span>${title}</span>
        <span class="chev">${shown ? '\u2212' : '+'}</span>
      </button>
      ${shown && html`<div class="stack" data-testid=${`venue-body-${id}`}>
                        ${children}
                      </div>`}
    </div>`;
}

/** True if there is anything worth drawing about this table. */
function anyTableFact(t) {
  return [t.size, t.cloth, t.price].some((v) => v);
}

/**
 * A link the phone's own map app will take.
 *
 * `geo:` is the Android intent that opens whatever map app somebody has
 * chosen as their default, which is what was asked for. iOS does not know
 * `geo:` and would do nothing at all, so it gets Apple Maps, and a desktop
 * browser gets OpenStreetMap — a link that silently does nothing is worse
 * than one that opens the wrong-but-working thing.
 *
 * The postcode is the query in every case: coordinates put the pin in the
 * right field, but a postcode is what somebody checks against the address
 * they were given, and what a satnav will accept if they retype it.
 */
function mapLink(venue) {
  const what = venue.postcode || venue.address || venue.name;
  const query = encodeURIComponent(what);
  const at = venue.lat != null && venue.lon != null
    ? `${venue.lat},${venue.lon}` : '0,0';
  const ua = navigator.userAgent || '';
  if (/iPhone|iPad|iPod|Macintosh/.test(ua)) {
    return venue.lat != null
      ? `https://maps.apple.com/?ll=${at}&q=${query}`
      : `https://maps.apple.com/?q=${query}`;
  }
  if (/Android/.test(ua)) return `geo:${at}?q=${query}`;
  return `https://www.openstreetmap.org/search?query=${query}`;
}
